// #3173: the magic-link scrub runs before anything else, so neither Sentry nor
// PostHog ever sees the token/email in window.location.href.
import './scrubMagicLinkUrl.js';
// Sentry must be imported before any other app modules
import './instrument.js';

import React from 'react'
import ReactDOM from 'react-dom/client'
import { PostHogProvider } from '@posthog/react'
import { GoogleOAuthProvider } from '@react-oauth/google'
import posthog from 'posthog-js'
import { captureEmailLinkArrival } from './lib/analytics/emailLinkArrival'
import App from './App.jsx'
import './i18n' // i18n must initialize before App renders (Issue #447)
import './index.css'
import { readAecOverride } from './lib/voice/captureConstraints'

// #3262 item 2 / #3258 — decode-aware backup redactor. The raw-text regex it
// replaces missed `?%74oken=JWT&%65mail=a%40b.test`, which URLSearchParams
// (and so the app) decodes as token/email. Parameter names are now compared
// after the same decoding URLSearchParams applies (percent-escapes and `+`),
// case-insensitively, and every occurrence of a repeated name is redacted.
// It also redacts the digest-unsubscribe token, which is a path segment
// (/unsubscribe/digest/<token>, and the /api/ route that shares the shape).
const REDACTED = 'REDACTED'
const SECRET_QUERY_KEYS = new Set(['token', 'email'])
const SECRET_QUERY_PATH = /\/auth\/verify(?:\/|$)/i
// Everything after the prefix, to the end of the path: a decoded `%2F` inside
// the token must not leave a tail of it unredacted.
const DIGEST_TOKEN_PATH = /(\/unsubscribe\/digest\/).+/i

function decodeUrlPart(value) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function decodeParamName(rawName) {
  try {
    return new URLSearchParams(`${rawName}=`).keys().next().value ?? rawName
  } catch {
    return rawName
  }
}

// Redacts token/email in one `a=b&c=d` string; every other segment is kept byte for byte.
function redactQueryString(query) {
  return query
    .split('&')
    .map((segment) => {
      const eq = segment.indexOf('=')
      const rawName = eq === -1 ? segment : segment.slice(0, eq)
      if (!SECRET_QUERY_KEYS.has(decodeParamName(rawName).toLowerCase())) return segment
      return `${rawName}=${REDACTED}`
    })
    .join('&')
}

export function redactMagicLinkUrl(url) {
  if (typeof url !== 'string' || url === '') return url

  const hashAt = url.indexOf('#')
  const beforeHash = hashAt === -1 ? url : url.slice(0, hashAt)
  const hash = hashAt === -1 ? '' : url.slice(hashAt + 1)
  const queryAt = beforeHash.indexOf('?')
  const base = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt)
  const query = queryAt === -1 ? null : beforeHash.slice(queryAt + 1)

  // Path tests run on the DECODED path, so `/auth/%76erify` counts as /auth/verify.
  const decodedBase = decodeUrlPart(base)
  const digestPath = DIGEST_TOKEN_PATH.test(decodedBase)
  const secretQueryPath = digestPath || SECRET_QUERY_PATH.test(decodedBase)
  if (!secretQueryPath) return url

  let outBase = base
  if (digestPath) {
    const redact = (_m, prefix) => `${prefix}${REDACTED}`
    outBase = DIGEST_TOKEN_PATH.test(base)
      ? base.replace(DIGEST_TOKEN_PATH, redact)
      // The prefix itself was percent-encoded: rewrite from the decoded form.
      : decodedBase.replace(DIGEST_TOKEN_PATH, redact)
  }
  const outQuery = query === null ? '' : `?${redactQueryString(query)}`
  const hashQueryAt = hash.indexOf('?')
  let outHash = ''
  if (hashAt !== -1) {
    outHash = hashQueryAt === -1
      ? `#${hash.includes('=') ? redactQueryString(hash) : hash}`
      : `#${hash.slice(0, hashQueryAt)}?${redactQueryString(hash.slice(hashQueryAt + 1))}`
  }
  return `${outBase}${outQuery}${outHash}`
}

export function sanitizePostHogUrlProps(props) {
  // Defence in depth behind scrubMagicLinkUrl.js (the root fix). It cannot reach
  // $snapshot_data or the /flags request's person_properties.
  for (const key of [
    '$current_url', '$referrer', '$initial_current_url', '$initial_referrer',
    '$session_entry_url', '$session_entry_referrer', '$pathname',
  ]) {
    props[key] = redactMagicLinkUrl(props[key])
  }
  return props
}

// Issue #1350/#1516 — capture the ?aec= experiment override on the FIRST page
// load, before any in-app navigation can strip the query param. readAecOverride
// persists it to sessionStorage, so opening e.g. /scenarios/124?aec=on then
// starting voice (which navigates to /chat/:id, dropping the param) still applies
// the chosen arm. This is what lets a tester self-serve with a plain link — no
// DevTools console needed. No-op unless ?aec= is present, so prod is unaffected.
readAecOverride()

// Initialize PostHog for user analytics and session recording
if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,  // Privacy: mask form inputs in recordings
      maskTextSelector: '[data-posthog-mask]',  // Additional masking selector
      maskNetworkRequestFn: (request) => ({ ...request, url: redactMagicLinkUrl(request.url) }),
    },
    sanitize_properties: sanitizePostHogUrlProps,
    // Respect Do Not Track browser setting
    respect_dnt: true,
  })
}

// Tagged email link (?src=…): one event before the router can redirect the
// tag away (see lib/analytics/emailLinkArrival.js). No-op without PostHog.
if (import.meta.env.VITE_POSTHOG_KEY) captureEmailLinkArrival(posthog)

// Export posthog instance for use in other modules (e.g., user identification)
export { posthog }

// Google OAuth Client ID - only wrap with provider if configured
const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      {googleClientId ? (
        <GoogleOAuthProvider clientId={googleClientId}>
          <App />
        </GoogleOAuthProvider>
      ) : (
        <App />
      )}
    </PostHogProvider>
  </React.StrictMode>,
)
